/**
 * @fileoverview Modal de tarefas pendentes do onboarding guiado. Mostra barra de
 * progresso, tarefas obrigatórias/opcionais com status e CTAs que fazem deep-link
 * para as telas de configuração. Minimizável (vira OnboardingLauncher). Estilo
 * alinhado ao design system (modalDesign / ConfirmationModal, MUI v4, dark-mode).
 */

import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Dialog, IconButton, makeStyles } from "@material-ui/core";
import { Minus, CheckCircle2, ChevronRight, SkipForward } from "lucide-react";

import { i18n } from "../../../translate/i18n";
import { createModalDesign } from "../../../styles/modalDesign";
import { useOnboarding } from "../../../context/Onboarding/OnboardingContext";
import { getTaskIcon, ctaStateKey } from "../taskConfig";
import AhaWizard from "../AhaWizard";

const useStyles = makeStyles(theme => {
  const md = createModalDesign(theme);
  const dark = theme.palette.type === "dark";
  return {
    dialog: {
      ...md.mdDialog,
      "& .MuiDialog-paper": {
        ...md.mdDialog["& .MuiDialog-paper"],
        width: "min(640px, calc(100vw - 32px))"
      }
    },
    header: { ...md.mdHeader, flexDirection: "column", alignItems: "stretch", gap: theme.spacing(1.5) },
    headerTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between" },
    title: md.mdTitle,
    subtitle: {
      fontSize: "0.85rem",
      color: dark ? "rgba(248,250,252,0.6)" : "#6b7280",
      marginTop: 2
    },
    headerActions: { display: "flex", gap: 4 },
    closeBtn: md.mdCloseBtn,
    progressWrap: { display: "flex", flexDirection: "column", gap: 6 },
    progressMeta: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "0.8rem",
      color: dark ? "rgba(248,250,252,0.72)" : "#475569",
      fontWeight: 600
    },
    progressTrack: {
      height: 8,
      borderRadius: 999,
      background: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
      overflow: "hidden"
    },
    progressFill: {
      height: "100%",
      borderRadius: 999,
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      transition: "width 600ms cubic-bezier(0.4,0,0.2,1)"
    },
    content: {
      padding: theme.spacing(2.5, 3, 3),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2.5),
      maxHeight: "calc(100vh - 280px)",
      overflowY: "auto",
      ...theme.scrollbarStylesSoft
    },
    groupTitle: md.mdSectionTitle,
    task: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      borderRadius: 14,
      border: dark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(15,23,42,0.07)",
      background: dark ? "rgba(255,255,255,0.02)" : "rgba(248,250,252,0.7)",
      transition: "all 180ms cubic-bezier(0.4,0,0.2,1)",
      "&:hover": {
        border: "1px solid rgba(91,124,255,0.35)"
      }
    },
    taskDone: { opacity: 0.7 },
    taskIcon: {
      width: 40,
      height: 40,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)",
      color: dark ? "#a5b4fc" : "#3D5CFD"
    },
    taskIconDone: {
      background: "rgba(34,197,94,0.12)",
      color: "#22c55e"
    },
    taskBody: { flex: 1, minWidth: 0 },
    taskLabel: {
      fontSize: "0.92rem",
      fontWeight: 600,
      color: dark ? "#f1f5f9" : "#111827"
    },
    taskDesc: {
      fontSize: "0.78rem",
      color: dark ? "rgba(248,250,252,0.55)" : "#6b7280",
      marginTop: 2,
      lineHeight: 1.4,
      whiteSpace: "normal",
      wordBreak: "break-word"
    },
    taskActions: { display: "flex", alignItems: "center", gap: 4, flexShrink: 0 },
    cta: {
      ...md.mdBtnPrimary,
      height: 32,
      minWidth: 92,
      fontSize: "0.8rem"
    },
    ctaGhost: {
      ...md.mdBtnGhost,
      height: 32,
      minWidth: 80,
      fontSize: "0.8rem"
    },
    skipBtn: { color: dark ? "rgba(248,250,252,0.4)" : "#9ca3af", padding: 6 },
    badgeRequired: {
      fontSize: 9,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: "#ef4444",
      marginLeft: 6
    }
  };
});

const OnboardingModal = () => {
  const classes = useStyles();
  const history = useHistory();
  const { progress, skipTask, setMinimized, startTour } = useOnboarding();
  const [ahaOpen, setAhaOpen] = useState(false);

  if (!progress || !progress.visible || progress.minimized) return null;

  const handleCta = task => {
    // AHA moment: a tarefa "criar IA" abre o wizard inline em vez de navegar.
    if (task.key === "createAi" && task.status !== "done") {
      setAhaOpen(true);
      return;
    }
    history.push(task.deepLink);
    setMinimized(true);
    // Tour guiado: realça o alvo na tela de destino (tarefas ainda não concluídas).
    if (task.status !== "done") {
      startTour(task);
    }
  };

  const renderTask = task => {
    const Icon = getTaskIcon(task.key);
    const done = task.status === "done";
    const label = i18n.t(`onboarding.tasks.${task.i18nKey}.label`);
    const desc = i18n.t(`onboarding.tasks.${task.i18nKey}.desc`);
    return (
      <div key={task.key} className={`${classes.task} ${done ? classes.taskDone : ""}`}>
        <div className={`${classes.taskIcon} ${done ? classes.taskIconDone : ""}`}>
          {done ? <CheckCircle2 size={20} /> : <Icon size={20} />}
        </div>
        <div className={classes.taskBody}>
          <div className={classes.taskLabel}>
            {label}
            {task.required && !done && (
              <span className={classes.badgeRequired}>{i18n.t("onboarding.requiredTag")}</span>
            )}
          </div>
          <div className={classes.taskDesc}>{desc}</div>
        </div>
        <div className={classes.taskActions}>
          <button
            type="button"
            className={done ? classes.ctaGhost : classes.cta}
            onClick={() => handleCta(task)}
          >
            {i18n.t(`onboarding.cta.${ctaStateKey(task.status)}`)}
            {!done && <ChevronRight size={15} />}
          </button>
          {!task.required && task.status === "pending" && (
            <IconButton
              size="small"
              className={classes.skipBtn}
              title={i18n.t("onboarding.cta.pular")}
              onClick={() => skipTask(task.key)}
            >
              <SkipForward size={15} />
            </IconButton>
          )}
        </div>
      </div>
    );
  };

  const required = progress.tasks.filter(t => t.required);
  const optional = progress.tasks.filter(t => !t.required);

  return (
    <>
    <Dialog open onClose={() => setMinimized(true)} className={classes.dialog} maxWidth={false}>
      <div className={classes.header}>
        <div className={classes.headerTop}>
          <div>
            <div className={classes.title}>{i18n.t("onboarding.title")}</div>
            <div className={classes.subtitle}>{i18n.t("onboarding.subtitle")}</div>
          </div>
          <div className={classes.headerActions}>
            <IconButton
              size="small"
              className={classes.closeBtn}
              title={i18n.t("onboarding.minimize")}
              onClick={() => setMinimized(true)}
            >
              <Minus size={18} />
            </IconButton>
          </div>
        </div>
        <div className={classes.progressWrap}>
          <div className={classes.progressMeta}>
            <span>
              {progress.requiredDone} {i18n.t("onboarding.of")} {progress.requiredTotal}{" "}
              {i18n.t("onboarding.stepsDone")}
            </span>
            <span>{progress.percent}%</span>
          </div>
          <div className={classes.progressTrack}>
            <div className={classes.progressFill} style={{ width: `${progress.percent}%` }} />
          </div>
        </div>
      </div>

      <div className={classes.content}>
        <div>
          <div className={classes.groupTitle}>{i18n.t("onboarding.groups.required")}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
            {required.map(renderTask)}
          </div>
        </div>
        {optional.length > 0 && (
          <div>
            <div className={classes.groupTitle}>{i18n.t("onboarding.groups.optional")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
              {optional.map(renderTask)}
            </div>
          </div>
        )}
      </div>
    </Dialog>
    <AhaWizard open={ahaOpen} onClose={() => setAhaOpen(false)} />
    </>
  );
};

export default OnboardingModal;
